import React, { useState, useCallback } from 'react';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { getABTests, getTestSuggestions, createABTest, startABTest, stopABTest } from '../services/api';

export default function ABTesting() {
  const { data: testsData, loading, reload } = useAPI(getABTests);
  const { data: suggestionsData } = useAPI(getTestSuggestions);
  const [creating, setCreating] = useState(false);

  const tests = testsData?.tests || [];
  const suggestions = suggestionsData?.suggestions || [];

  const activeTests = tests.filter(t => t.status === 'active');
  const completedTests = tests.filter(t => t.status === 'completed');
  const draftTests = tests.filter(t => t.status === 'draft');

  const handleQuickCreate = async (suggestion) => {
    setCreating(true);
    try {
      await createABTest({
        name: suggestion.name,
        description: suggestion.description,
        test_type: suggestion.type,
        primary_metric: suggestion.metric,
        control_name: suggestion.control.name,
        control_desc: suggestion.control.desc,
        treatment_name: suggestion.treatment.name,
        treatment_desc: suggestion.treatment.desc,
        min_sample: 30,
        allocation_pct: 50,
      });
      reload();
    } catch (err) {
      alert('Error creating test: ' + err.message);
    }
    setCreating(false);
  };

  const handleStart = async (testId) => {
    try {
      await startABTest(testId);
      reload();
    } catch (err) {
      alert('Error starting test: ' + err.message);
    }
  };

  const handleStop = async (testId) => {
    try {
      await stopABTest(testId);
      reload();
    } catch (err) {
      alert('Error stopping test: ' + err.message);
    }
  };

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading A/B tests...</div>;

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>A/B Testing</h1>
      <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
        Compare repair strategies to find the most cost-effective approaches
      </p>

      {/* KPI Cards */}
      <div style={styles.cardGrid}>
        <StatCard label="Active Tests" value={activeTests.length} color="#00b894" />
        <StatCard label="Completed" value={completedTests.length} color="#6c5ce7" />
        <StatCard label="Draft" value={draftTests.length} color="#636e72" />
        <StatCard label="Total Tests" value={tests.length} color="#0984e3" />
      </div>

      {/* Active tests */}
      {activeTests.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={styles.sectionTitle}>Active Experiments</h2>
          {activeTests.map(test => (
            <TestCard key={test.id} test={test} onStop={handleStop} />
          ))}
        </div>
      )}

      {/* Completed tests with results */}
      {completedTests.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={styles.sectionTitle}>Completed Experiments</h2>
          {completedTests.map(test => (
            <TestCard key={test.id} test={test} />
          ))}
        </div>
      )}

      {/* Draft tests */}
      {draftTests.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={styles.sectionTitle}>Draft Experiments</h2>
          {draftTests.map(test => (
            <TestCard key={test.id} test={test} onStart={handleStart} />
          ))}
        </div>
      )}

      {/* Suggested tests */}
      <h2 style={styles.sectionTitle}>Suggested Experiments</h2>
      <p style={{ fontSize: 13, color: '#636e72', marginBottom: 12 }}>
        Pre-configured experiments based on AirTouch operational data. Click to create.
      </p>
      <div style={styles.suggestionsGrid}>
        {suggestions.map((s, i) => (
          <div key={i} style={styles.suggestionCard}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{s.name}</div>
            <div style={{ fontSize: 12, color: '#636e72', marginBottom: 10, lineHeight: 1.5 }}>
              {s.description}
            </div>
            <div style={styles.vsRow}>
              <div style={styles.vsControl}>
                <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7 }}>CONTROL</div>
                <div style={{ fontSize: 12 }}>{s.control?.name}</div>
              </div>
              <div style={styles.vsLabel}>VS</div>
              <div style={styles.vsTreatment}>
                <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7 }}>TREATMENT</div>
                <div style={{ fontSize: 12 }}>{s.treatment?.name}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <span style={{ fontSize: 12, color: '#00b894', fontWeight: 600 }}>
                {s.estimated_savings}
              </span>
              <button
                onClick={() => handleQuickCreate(s)}
                disabled={creating}
                style={styles.createBtn}
              >
                Create Test
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestCard({ test, onStart, onStop }) {
  const result = test.result;
  const variants = test.variants || [];
  const control = variants.find(v => v.is_control);
  const treatment = variants.find(v => !v.is_control);

  return (
    <div style={styles.testCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{test.name}</span>
            <span style={{
              ...styles.statusBadge,
              background: test.status === 'active' ? '#d4edda' : test.status === 'completed' ? '#e8daef' : '#e9ecef',
              color: test.status === 'active' ? '#155724' : test.status === 'completed' ? '#6c3483' : '#636e72',
            }}>
              {test.status}
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#636e72', marginTop: 4 }}>
            {test.type} | Metric: {test.metric} | {test.duration_days}d running
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onStart && test.status === 'draft' && (
            <button onClick={() => onStart(test.id)} style={{ ...styles.actionBtn, background: '#00b894' }}>
              Start
            </button>
          )}
          {onStop && test.status === 'active' && (
            <button onClick={() => onStop(test.id)} style={{ ...styles.actionBtn, background: '#d63031' }}>
              Stop
            </button>
          )}
        </div>
      </div>

      {/* Variant comparison */}
      {control && treatment && (
        <div style={styles.variantComparison}>
          <div style={styles.variantBox}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#636e72' }}>CONTROL: {control.name}</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{control.mean}</div>
            <div style={{ fontSize: 11, color: '#999' }}>n={control.sample_size}</div>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#636e72', alignSelf: 'center' }}>vs</div>
          <div style={styles.variantBox}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6c5ce7' }}>TREATMENT: {treatment.name}</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{treatment.mean}</div>
            <div style={{ fontSize: 11, color: '#999' }}>n={treatment.sample_size}</div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{
          ...styles.resultBar,
          background: result.recommendation === 'adopt' ? '#d4edda' :
                      result.recommendation === 'reject' ? '#ffe0e0' : '#f8f9fa',
          borderColor: result.recommendation === 'adopt' ? '#00b894' :
                       result.recommendation === 'reject' ? '#d63031' : '#dee2e6',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {result.recommendation === 'adopt' ? 'Adopt Treatment' :
                 result.recommendation === 'reject' ? 'Keep Control' : 'Continue Testing'}
              </span>
              {result.improvement !== 0 && (
                <span style={{
                  marginLeft: 8, fontWeight: 700,
                  color: result.improvement > 0 ? '#00b894' : '#d63031',
                }}>
                  {result.improvement > 0 ? '+' : ''}{result.improvement}%
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#636e72' }}>
              p={result.p_value} | {result.is_significant ? 'Significant' : 'Not significant'}
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#636e72', marginTop: 4 }}>
            {result.details}
          </div>
        </div>
      )}

      {/* Progress bar if not ready */}
      {!test.ready_for_analysis && test.status === 'active' && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#e17055' }}>
          Collecting data... Need more observations before analysis.
        </div>
      )}
    </div>
  );
}

const styles = {
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '20px 0',
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  suggestionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
  },
  suggestionCard: {
    padding: 16,
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0',
  },
  vsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  vsControl: {
    flex: 1,
    padding: '8px 10px',
    background: '#f8f9fa',
    borderRadius: 6,
    fontSize: 12,
  },
  vsLabel: {
    fontWeight: 700,
    fontSize: 12,
    color: '#636e72',
  },
  vsTreatment: {
    flex: 1,
    padding: '8px 10px',
    background: '#f0ebff',
    borderRadius: 6,
    fontSize: 12,
  },
  createBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    background: '#6c5ce7',
    color: '#fff',
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer',
  },
  testCard: {
    padding: 16,
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    marginBottom: 12,
  },
  statusBadge: {
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
  },
  actionBtn: {
    padding: '4px 12px',
    borderRadius: 4,
    border: 'none',
    color: '#fff',
    fontWeight: 600,
    fontSize: 11,
    cursor: 'pointer',
  },
  variantComparison: {
    display: 'flex',
    gap: 16,
    marginTop: 12,
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  variantBox: {
    flex: 1,
    padding: '12px 16px',
    background: '#f8f9fa',
    borderRadius: 8,
    textAlign: 'center',
  },
  resultBar: {
    marginTop: 12,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid',
  },
};
